import { FormSectionCard } from '@/components/tax-form/FormSectionCard';
import { getStateOptions } from '@/components/tax-form/formFieldHelpers';
import { INDIVIDUAL_SECTIONS } from '@/components/tax-form/individualFormConfig';
import { Button } from '@/components/ui/button';
import { DatePickerInput } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PERSON_TYPE } from '@/constants/formConstants';
import type { PersonalInfoItem, ResidencyDetailItem } from '@/types/form';
import { Plus, Trash2 } from 'lucide-react';

type PersonKey = typeof PERSON_TYPE.TAXPAYER | typeof PERSON_TYPE.SPOUSE;

function emptyRow(personType: PersonKey): ResidencyDetailItem {
  return { personType };
}

function splitByPerson(list: ResidencyDetailItem[]): {
  taxpayer: ResidencyDetailItem[];
  spouse: ResidencyDetailItem[];
} {
  const taxpayer: ResidencyDetailItem[] = [];
  const spouse: ResidencyDetailItem[] = [];
  for (const row of list) {
    if (row.personType === PERSON_TYPE.SPOUSE) {
      spouse.push({ ...row, personType: PERSON_TYPE.SPOUSE });
    } else {
      taxpayer.push({ ...row, personType: PERSON_TYPE.TAXPAYER });
    }
  }
  return {
    taxpayer: taxpayer.length > 0 ? taxpayer : [emptyRow(PERSON_TYPE.TAXPAYER)],
    spouse,
  };
}

function flatten(
  taxpayer: ResidencyDetailItem[],
  spouse: ResidencyDetailItem[]
): ResidencyDetailItem[] {
  return [
    ...taxpayer.map((r) => ({ ...r, personType: PERSON_TYPE.TAXPAYER as PersonKey })),
    ...spouse.map((r) => ({ ...r, personType: PERSON_TYPE.SPOUSE as PersonKey })),
  ];
}

function StatePeriodRow({
  row,
  onChange,
  onRemove,
  canRemove,
}: {
  row: ResidencyDetailItem;
  onChange: (patch: Partial<ResidencyDetailItem>) => void;
  onRemove?: () => void;
  canRemove: boolean;
}) {
  const stateOptions = getStateOptions();

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-end">
      <div className="space-y-2">
        <Label>States Resided</Label>
        <Select
          value={row.state ?? ''}
          onValueChange={(v) => onChange({ state: v as ResidencyDetailItem['state'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select state" />
          </SelectTrigger>
          <SelectContent>
            {stateOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Period of stay (start)</Label>
        <DatePickerInput
          value={row.startDate}
          onChange={(v) => onChange({ startDate: v })}
          placeholder="MM/DD/YYYY"
        />
      </div>
      <div className="space-y-2">
        <Label>Period of stay (end)</Label>
        <DatePickerInput
          value={row.endDate}
          onChange={(v) => onChange({ endDate: v })}
          placeholder="MM/DD/YYYY"
        />
      </div>
      {canRemove && onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={onRemove}
          title="Remove state"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export interface ResidencyDetailsSectionProps {
  residencyDetails?: ResidencyDetailItem[];
  personalInformation?: PersonalInfoItem[];
  onChange: (residencyDetails: ResidencyDetailItem[]) => void;
}

export function ResidencyDetailsSection({
  residencyDetails,
  personalInformation,
  onChange,
}: ResidencyDetailsSectionProps) {
  const hasSpouseOnForm = (personalInformation ?? []).some(
    (p) => p.personType === PERSON_TYPE.SPOUSE
  );

  const { taxpayer, spouse } = splitByPerson(
    Array.isArray(residencyDetails) ? residencyDetails : []
  );

  const commit = (nextTaxpayer: ResidencyDetailItem[], nextSpouse: ResidencyDetailItem[]) => {
    onChange(flatten(nextTaxpayer, nextSpouse));
  };

  const updatePersonRow = (
    person: PersonKey,
    rowIndex: number,
    patch: Partial<ResidencyDetailItem>
  ) => {
    const rows = person === PERSON_TYPE.SPOUSE ? [...spouse] : [...taxpayer];
    if (!rows[rowIndex]) return;
    rows[rowIndex] = { ...rows[rowIndex], ...patch, personType: person };
    if (person === PERSON_TYPE.SPOUSE) {
      commit(taxpayer, rows);
    } else {
      commit(rows, spouse);
    }
  };

  const addPersonState = (person: PersonKey) => {
    if (person === PERSON_TYPE.SPOUSE) {
      commit(taxpayer, [...spouse, emptyRow(PERSON_TYPE.SPOUSE)]);
    } else {
      commit([...taxpayer, emptyRow(PERSON_TYPE.TAXPAYER)], spouse);
    }
  };

  const removePersonRow = (person: PersonKey, rowIndex: number) => {
    if (person === PERSON_TYPE.TAXPAYER) {
      const rows = taxpayer.filter((_, i) => i !== rowIndex);
      commit(rows.length > 0 ? rows : [emptyRow(PERSON_TYPE.TAXPAYER)], spouse);
      return;
    }
    commit(
      taxpayer,
      spouse.filter((_, i) => i !== rowIndex)
    );
  };

  const sectionMeta = INDIVIDUAL_SECTIONS.find((s) => s.key === 'residencyDetails');
  if (!sectionMeta) return null;

  const showSpouseColumn = hasSpouseOnForm || spouse.length > 0;

  return (
    <FormSectionCard title={sectionMeta.title} description={sectionMeta.description}>
      <div className="space-y-6">
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="bg-muted/60">
                <th
                  className={`border-b px-3 py-2 text-left font-semibold${showSpouseColumn ? ' border-r' : ''}`}
                >
                  Taxpayer
                </th>
                {showSpouseColumn && (
                  <th className="border-b px-3 py-2 text-left font-semibold">Spouse</th>
                )}
              </tr>
              <tr className="bg-muted/30 text-muted-foreground">
                <th
                  className={`border-b px-3 py-1.5 text-left font-medium text-xs${showSpouseColumn ? ' border-r' : ''}`}
                >
                  States Resided / Period of stay (Start and End Date)
                </th>
                {showSpouseColumn && (
                  <th className="border-b px-3 py-1.5 text-left font-medium text-xs">
                    States Resided / Period of stay (Start and End Date)
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <tr className="align-top">
                <td
                  className={`px-3 py-3 space-y-3${showSpouseColumn ? ' border-r' : ''}`}
                >                  {taxpayer.map((row, rowIndex) => (
                    <StatePeriodRow
                      key={`tp-${rowIndex}`}
                      row={row}
                      canRemove={taxpayer.length > 1}
                      onRemove={() => removePersonRow(PERSON_TYPE.TAXPAYER, rowIndex)}
                      onChange={(patch) =>
                        updatePersonRow(PERSON_TYPE.TAXPAYER, rowIndex, patch)
                      }
                    />
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addPersonState(PERSON_TYPE.TAXPAYER)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add another state
                  </Button>
                </td>
                {showSpouseColumn && (
                  <td className="px-3 py-3 space-y-3">
                    {spouse.length === 0 ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addPersonState(PERSON_TYPE.SPOUSE)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add spouse residency
                      </Button>
                    ) : (
                      <>
                        {spouse.map((row, rowIndex) => (
                          <StatePeriodRow
                            key={`sp-${rowIndex}`}
                            row={row}
                            canRemove
                            onRemove={() => removePersonRow(PERSON_TYPE.SPOUSE, rowIndex)}
                            onChange={(patch) =>
                              updatePersonRow(PERSON_TYPE.SPOUSE, rowIndex, patch)
                            }
                          />
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addPersonState(PERSON_TYPE.SPOUSE)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add another state
                        </Button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>

        {!showSpouseColumn && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addPersonState(PERSON_TYPE.SPOUSE)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add spouse residency
          </Button>
        )}
      </div>
    </FormSectionCard>
  );
}
